import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { Collapse } from 'reactstrap';
import dayjs from 'dayjs';

import { AuthTags } from '~constants/authTags';
import { AppointmentStatuses } from '~constants/maps';

import { dateInputFieldFormat } from '~libs/dayjs';
import { useSearch } from '~libs/searchParams';

import { Button } from '~components';
import ContainerHeader from '~components/ContainerHeader';
import ErrorResponse from '~components/ErrorResponse';
import ExportDialog from '~components/ExportDialog';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faBusinessTime, faFileExcel, faMagnifyingGlass, faPlus } from '~components/icon';
import { Row, SortState, TableView } from '~components/table-view';

import exportFields from '~appointments/forms/appointments/export';
import { getColumns } from '~appointments/forms/group/list';
import searchFields from '~appointments/forms/group/search';

import { exportAll, getAll, getEstimate } from '~appointments/actions/appointments';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getStatuses } from '~appointments/actions/statuses';
import { getAll as getClientPackages } from '~main/actions/clients/clientPackages';
import { getAll as getClientReferrers } from '~main/actions/clients/clientReferrers';
import { setTitle } from '~main/actions/login';

import { clearAppointments, clearMessage } from '~appointments/reducers/appointments';

import {
  selectAppointmentLoading,
  selectAppointmentRowEstimate,
  selectAppointments,
  selectAppointmentsTotal,
  selectErrorResponse,
} from '~appointments/selectors/appointments';

const GroupAppointments: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appointments = useAppSelector(selectAppointments);
  const appointmentsTotal = useAppSelector(selectAppointmentsTotal);
  const appointmentRowEstimate = useAppSelector(selectAppointmentRowEstimate);
  const errorResponse = useAppSelector(selectErrorResponse);
  const appointmentLoading = useAppSelector(selectAppointmentLoading);

  const initialSortState: SortState = [
    {
      id: 'last_updated',
      direction: 'desc',
    },
  ];

  const initialSearchState: any = {
    date: { gte: dayjs().format(dateInputFieldFormat) },
    status_id: Object.entries(AppointmentStatuses)
      .map(([, value]) => (value < 8 ? value : false))
      .filter(Boolean),
  };

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('search', initialSearchState);
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

  const [params, setParams] = useState({
    repeat: false,
    group_session_id: 1,
    sort: initialSortState.map(({ id, direction }) => `${id}.${direction}`),
    'client.disabled': false,
    limit: 20,
  });

  const isFirstLoad = useRef(true);

  useEffect(() => {
    setTitle('Group Appointments');

    // Get group appointments on mount
    load();

    // Get lookups on mount
    dispatch(
      getServiceTypes({
        deleted: {
          or: [true, false],
        },
        sort: ['deleted', 'name'],
      }),
    );
    dispatch(getStatuses());
    dispatch(getClientPackages({ sort: ['name'], deleted: false }));
    dispatch(getClientReferrers({ sort: ['name'], deleted: false }));

    // Clean up component on unmount, clear reducer, etc.
    return () => {
      dispatch(clearAppointments());
      dispatch(clearMessage());
    };
  }, []);

  useEffect(() => {
    // If first load, do not run another load.
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    if (searchParams !== initialSearchState) load();
  }, [searchParams]);

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };

    // Keep search and load params separate as search can be cleared. Don't want to retain values when nothing is passed back.
    dispatch(getAll({ ...newParams, ...searchParams }));
    setParams(newParams);
  };

  const sort = (params: SortState = []) => {
    load({
      sort: params.filter(({ direction }) => direction !== null).map(({ id, direction }) => `${id}.${direction}`),
    });
  };

  const download = (params: { [key: string]: any }, onProgress: (value: any) => void) =>
    exportAll({ params: { group_session_id: 1, repeat: false, ...params }, onProgress });

  const search = (values: { [key: string]: any }) => {
    setSearchParams(values);
  };

  const estimate = async (values: { [key: string]: any }) =>
    dispatch(
      getEstimate({
        group_session_id: 1,
        repeat: false,
        ...values,
      }),
    );

  const isLoading = appointmentLoading === 'pending';
  const authTag = AuthTags.APPOINTMENTS.UPDATE;

  const openAppointment = useCallback((row: Row) => navigate(`/appointments/${row.id}`), [navigate]);
  const getAppointmentHref = useCallback((row: Row) => `/appointments/${row.id}`, []);
  const columns = useMemo(() => getColumns(openAppointment, getAppointmentHref), [openAppointment, getAppointmentHref]);

  return (
    <div className="p-2">
      <ContainerHeader
        icon={faBusinessTime}
        iconSize="1x"
        className="header group-appointment text-white"
        dataCy="group-appointments-header"
      >
        Group Appointments
      </ContainerHeader>
      <ErrorResponse message={errorResponse} />
      <div className="nav d-flex justify-content-between bg-light p-2 mt-2 mb-2">
        <div>
          <Button
            authTag={authTag}
            size="sm"
            color="success"
            className="ms-2 me-2"
            href={'/appointments/groups/new'}
            data-cy="group-appointments-new-button"
          >
            <Icon className="me-2" icon={faPlus} />
            Add Group Appointment
          </Button>
          <Button
            authTag={authTag}
            size="sm"
            color="success"
            className="ms-2 me-2"
            href={'/appointments/groups/repeats/new'}
            data-cy="group-appointments-new-repeat-button"
          >
            <Icon className="me-2" icon={faPlus} />
            Add Group Repeat Appointment
          </Button>
        </div>
        <div>
          <Button size="sm" className="me-2" onClick={() => setSearchBarOpen(!searchBarOpen)}>
            <Icon className="me-2" icon={faMagnifyingGlass} />
            {searchBarOpen ? 'Hide' : 'Search'}
          </Button>
          <Button size="sm" className="me-2" onClick={() => setExportDialogOpen(!exportDialogOpen)}>
            <Icon className="me-2" icon={faFileExcel} />
            Export
          </Button>
        </div>
      </div>
      <ExportDialog
        isOpen={exportDialogOpen}
        fields={exportFields}
        estimate={appointmentRowEstimate}
        openingValues={searchParams}
        onClose={() => setExportDialogOpen(false)}
        onEstimate={estimate}
        onDownload={(params, onProgress) => download(params, onProgress)}
      />
      <Collapse isOpen={searchBarOpen}>
        <SearchBar
          fields={searchFields}
          initialValues={searchParams}
          storageKey="group-appointments"
          onSubmit={search}
        />
      </Collapse>
      <TableView
        id="group-appointments"
        data={appointments as Row[]}
        columns={columns}
        readonly
        striped
        isLoading={isLoading}
        emptyIcon={faBusinessTime}
        emptyMessage={'No appointments found, please add one!'}
        allowSort={true}
        initialSortState={initialSortState}
        onSort={sort}
        allowPagination={true}
        onLoadMore={() => load({ limit: params.limit + 20 })}
        showCount={true}
        totalCount={appointmentsTotal}
      />
    </div>
  );
};

export default GroupAppointments;
